/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    NOTIFICATIONS_FIX_V3
 * Date:         2025-12-30
 * Modified By:  AI
 *
 * Description:
 * Reverted to .ts by moving JSX to toastComponents.tsx.
 * This should resolve the parsing error reported by ESLint.
 */

import React from "react";
import { toast } from "sonner";
import { sanitizeServerError } from "./notifications/errorSanitizer";
import { 
  showSuccess, 
  showError, 
  showInfo, 
  showWarning, 
  showCustomError 
} from "./notifications/toastComponents";

function formatMessage(message: string): string {
  if (!message || typeof message !== "string") return "An error occurred. Please try again.";
  let formatted = message.trim();
  if (formatted.length > 0) {
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }
  if (formatted.length > 0) {
    const lastChar = formatted[formatted.length - 1];
    if (![".", "!", "?"].includes(lastChar)) {
      formatted += ".";
    }
  }
  return formatted;
}

export function getErrorMessage(error: any, fallback?: string): string {
  const sanitized = sanitizeServerError(error);
  if (sanitized) return sanitized;
  if (fallback) return fallback;
  if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message;
  }
  return "An error occurred. Please try again.";
}

export interface NotifyHelper {
  success: (message: string) => void;
  error: (error: any, fallback?: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
  loading: (message: string) => void;
  dismiss: (toastId: string | number) => void;
  customError: (content: React.ReactNode, options?: { duration?: number }) => string | number;
}

export const notify: NotifyHelper = {
  success: (message: string) => {
    showSuccess(message, formatMessage(message));
  },
  error: (error: any, fallback?: string) => {
    const message = formatMessage(getErrorMessage(error, fallback));
    showError(message);
  },
  info: (message: string) => {
    showInfo(formatMessage(message));
  },
  warning: (message: string) => {
    showWarning(formatMessage(message));
  },
  loading: (message: string) => {
    toast.loading(formatMessage(message), {
      style: {
        maxWidth: "70vw",
        width: "fit-content",
        whiteSpace: "pre-wrap",
        padding: "12px 16px",
        borderRadius: "8px",
      },
    });
  },
  dismiss: (toastId: string | number) => {
    toast.dismiss(toastId);
  },
  customError: (content: React.ReactNode, options?: { duration?: number }) => {
    return showCustomError(content, options);
  },
};
